/**
 * Task schema — an attention item.
 *
 * Command Center's attention/communication layer (ADR-0057, ADR-0119). A
 * floated matter is one or more **Task** rows, each its own card ("one card
 * per attention"): to-dos live on the Dashboard, private watches pin on The
 * Surface.
 *
 * Two kinds of Task, distinguished by a single fact — no `kind` field:
 *   - **Watch** — `assignee` is null. An eye on a matter, always the creator's
 *     own (`assigner`). Carries a why-note (`body`); no close loop.
 *   - **To-do** — `assignee` is set. Work that must get done. Carries the
 *     instruction (`body`) and the close loop (Open → Done → Confirm).
 *
 * Close state is carried by two existing Airtable fields, so no schema change:
 *   - `status` — open | in progress | done (Tasks.Status singleSelect).
 *   - `completed` — null while the attention is **live** (shows on The
 *     Surface); set when the attention is **cleared** (a confirmed to-do, or an
 *     un-watched watch). Clearing via `completed` (rather than deleting the row)
 *     preserves the thread for the deferred done-with-history pass, and works
 *     around AirtableClient having no delete primitive.
 *
 * Projected from the Airtable Tasks table (see docs/airtable-schema.md); the
 * adapter populates `created` from the record envelope's `createdTime`.
 *
 * Spec: docs/port-notes/surface.md (evicted 2026-08-29, #775 round 10) · ADR-0057
 */

import { z } from "zod";

/** Tasks.Status singleSelect — the three states defined in CONTEXT.md. */
export const TASK_STATUSES = ["open", "in progress", "done"] as const;
export const TaskStatusSchema = z.enum(TASK_STATUSES);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

/** Tasks.Priority singleSelect. Modeled now; left unwired in v1 UI. */
export const TASK_PRIORITIES = ["Urgent", "High", "Normal", "Low"] as const;
export const TaskPrioritySchema = z.enum(TASK_PRIORITIES);
export type TaskPriority = z.infer<typeof TaskPrioritySchema>;

/**
 * What a Task is *about* — a polymorphic pointer (Dashboard Slice B): a Task
 * can pin a Lead, a Firm, a User, a free-text reference, or
 * nothing. Storage invariant: exactly one of the link/Reference fields is
 * populated per row, or all are null (which is "no about"). The adapter
 * enforces this; Airtable can't.
 *
 *   - `lead` / `firm` / `user` — Airtable record id of the linked record.
 *   - `misc` — free-text reference (e.g. `Slack: <url>`, `Text re: Mancano`).
 *
 * The Surface (Operations) reads only `kind === "lead"`; Dashboard reads all
 * kinds and sections by `kind`.
 */
export const TaskAboutSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("lead"), id: z.string().min(1) }),
  z.object({ kind: z.literal("firm"), id: z.string().min(1) }),
  z.object({ kind: z.literal("user"), id: z.string().min(1) }),
  z.object({ kind: z.literal("misc"), reference: z.string().min(1) }),
]);
export type TaskAbout = z.infer<typeof TaskAboutSchema>;

/**
 * A Task as the surface consumes it. Optional Airtable fields project to
 * `null` (not `undefined`), matching the Lead projection convention.
 */
export const TaskSchema = z.object({
  /** Airtable record ID. */
  id: z.string(),
  /** ISO-8601 datetime — Airtable's system createdTime (when assigned). */
  created: z.string(),

  /** Primary field — a short readable label (derived, e.g. "To-do: Mancano"). */
  title: z.string(),
  /** The why (watch) or the instruction (to-do). Markdown by convention. */
  body: z.string().nullable(),

  /** Who does it (email). **Null marks a watch** — the row has no assignee. */
  assignee: z.string().nullable(),
  /** Who created it (email) — for a watch, the watcher. */
  assigner: z.string(),

  status: TaskStatusSchema,
  priority: TaskPrioritySchema,

  /** Assigner-set hard deadline (YYYY-MM-DD) or null. */
  dueBy: z.string().nullable(),
  /** Assignee-set self-paced commit (YYYY-MM-DD) or null. */
  committedBy: z.string().nullable(),
  /** Cleared marker (YYYY-MM-DD): **null = live**, set = cleared (off Surface). */
  completed: z.string().nullable(),
  /** Assignee-set snooze date (YYYY-MM-DD): rows whose `snoozedUntil` is
   *  strictly after today are filtered out of the default Dashboard view; the
   *  "Show snoozed" toggle re-surfaces them. Null = not snoozed. Dashboard
   *  Slice H (ADR-0088). */
  snoozedUntil: z.string().nullable(),

  /**
   * What the Task is about (Dashboard Slice B). `null` is "no about" — a pure
   * reminder. The Surface filters to `about?.kind === "lead"`; Dashboard
   * groups by `kind`.
   */
  about: TaskAboutSchema.nullable(),
  /**
   * @deprecated Use `about` instead. Kept one release for back-compat with the
   * Surface composer and the Operations write paths. Projected from `about`
   * when `about?.kind === "lead"`, else null.
   */
  leadRecordId: z.string().nullable(),
  /** Owning component (singleSelect). Operations for all v1 tasks. */
  component: z.string().nullable(),
});
export type Task = z.infer<typeof TaskSchema>;

/** A message in a Task's thread — projected from the Task Messages table. */
export const TaskMessageSchema = z.object({
  /** Airtable record ID. */
  id: z.string(),
  /** ISO-8601 datetime — Airtable's system createdTime. */
  timestamp: z.string(),
  /** Author email. */
  author: z.string(),
  /** Message body. Markdown by convention. */
  body: z.string(),
  /** Linked Task record ID (single-clamped), or null. */
  taskRecordId: z.string().nullable(),
});
export type TaskMessage = z.infer<typeof TaskMessageSchema>;

/** The two kinds of attention, derived from whether an assignee is present. */
export type TaskKind = "watch" | "todo";

/** Empty-string and null both mean "no assignee" (Airtable omits blanks). */
function hasAssignee(assignee: string | null): boolean {
  return assignee !== null && assignee.trim() !== "";
}

/** A watch (no assignee) vs a to-do (assignee set). */
export function taskKind(task: Pick<Task, "assignee">): TaskKind {
  return hasAssignee(task.assignee) ? "todo" : "watch";
}

/** True while the attention is live (shows on The Surface). */
export function isLive(task: Pick<Task, "completed">): boolean {
  return task.completed === null;
}

/**
 * The viewer-relative attention registers, in priority order — top is the most
 * live a concern for the viewer (ADR-0057). The Dashboard groups its to-dos by
 * them (`registerFor`); the Surface holds watches only.
 *
 * A watch outranks *Waiting on them*: my deliberate eye beats a delegated item
 * parked in the other operator's court.
 */
export const TaskRegisterSchema = z.enum([
  "needs-my-action",
  "im-watching",
  "waiting-on-them",
  "theyre-watching",
]);
export type TaskRegister = z.infer<typeof TaskRegisterSchema>;
/** The registers in priority order (the schema's option order). */
export const TASK_REGISTERS = TaskRegisterSchema.options;

/** Display labels for each register (viewer-relative phrasing). */
export const TASK_REGISTER_LABELS: Record<TaskRegister, string> = {
  "needs-my-action": "Needs my action",
  "im-watching": "I'm watching",
  "waiting-on-them": "Waiting on them",
  "theyre-watching": "They're watching",
};

// ---------------------------------------------------------------------------
// API contracts — the request/response shapes for /api/operations/tasks.
// ---------------------------------------------------------------------------

/**
 * Create a watch or a to-do. The request carries a `kind` discriminator for
 * watch-vs-todo; storage derives the same fact from whether `assignee` is set.
 * `title` is optional — the server derives a minimal default when omitted (the
 * web sends a matter-labelled one, e.g. "To-do: Mancano").
 *
 * The Task's *about* is supplied two ways for one release:
 *   - New: `about: { kind: "lead" | "firm" | "user" | "misc", ... }`
 *     (Dashboard Slice B; the canonical shape).
 *   - Legacy: `leadRecordId: string` (ADR-0057's original shape; the route
 *     normalises it to `about: { kind: "lead", id: leadRecordId }`).
 *
 * Exactly one of `about` or `leadRecordId` must be supplied. Watches today are
 * still Lead-attached (the Surface watches matters). Todos can target any
 * `about` kind — that's the Dashboard generalisation.
 *
 * `dueBy` (assigner's hard date) is optional. `notifyOnSlack` (Slice E hook,
 * wired then) is optional and defaults to false; the route accepts it as a
 * no-op until the notifier path lands.
 */
const CreateTaskBaseShape = {
  title: z.string().min(1).optional(),
  /** Assigner's hard deadline (YYYY-MM-DD). Optional. */
  dueBy: z.string().optional(),
  /** Tasks.Priority — optional; server defaults to Normal. Dashboard Slice G
   *  surfaces the field; the writer has carried the default since Slice E. */
  priority: TaskPrioritySchema.optional(),
  /** Opt-in Slack ping to the assignee at create time. Slice E hook. */
  notifyOnSlack: z.boolean().optional(),
};

export const CreateTaskRequestSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("watch"),
    // Watches stay Lead-attached for now (the Surface watches matters). Accept
    // either shape and normalise downstream.
    about: TaskAboutSchema.optional(),
    leadRecordId: z.string().min(1).optional(),
    /** The why-note. Optional. */
    note: z.string().optional(),
    ...CreateTaskBaseShape,
  }),
  z.object({
    kind: z.literal("todo"),
    about: TaskAboutSchema.optional(),
    leadRecordId: z.string().min(1).optional(),
    /** Who must do it. */
    assignee: z.string().email(),
    /** The instruction. */
    instruction: z.string().min(1),
    ...CreateTaskBaseShape,
  }),
]);
export type CreateTaskRequest = z.infer<typeof CreateTaskRequestSchema>;

/**
 * Set the assignee-side commit ("I can have it done by Y"). Independent of
 * Due By. Empty/missing date is rejected — use a clear date or don't call.
 */
export const SetCommitRequestSchema = z.object({
  /** ISO YYYY-MM-DD. */
  committedBy: z.string().min(1),
});
export type SetCommitRequest = z.infer<typeof SetCommitRequestSchema>;

/**
 * Close-loop actions on a task (PATCH). `clear` = confirm a to-do or un-watch.
 * `set-commit` is the assignee-side date commit (Dashboard Slice B); it carries
 * its own body shape (SetCommitRequestSchema) — the generic note field is
 * ignored for that action.
 */
export const TASK_ACTIONS = [
  "mark-done",
  "send-back",
  "clear",
  "set-commit",
  "snooze",
  "unsnooze",
] as const;
export const TaskActionRequestSchema = z.object({
  action: z.enum(TASK_ACTIONS),
  /** Optional note posted to the thread as part of the action (e.g. the
   *  "what did you do?" write-up on mark-done, or a send-back reason). */
  note: z.string().optional(),
});
export type TaskActionRequest = z.infer<typeof TaskActionRequestSchema>;

/**
 * Assignee snoozes a task until `snoozedUntil` (ISO YYYY-MM-DD). Empty/missing
 * date is rejected — `unsnooze` clears the field, so a "snooze with no date"
 * has no meaning. Dashboard Slice H (ADR-0088).
 */
export const SetSnoozeRequestSchema = z.object({
  snoozedUntil: z.string().min(1),
});
export type SetSnoozeRequest = z.infer<typeof SetSnoozeRequestSchema>;

/** Append one message to a task's thread. */
export const AddMessageRequestSchema = z.object({
  body: z.string().min(1),
});
export type AddMessageRequest = z.infer<typeof AddMessageRequestSchema>;

/** GET a task's thread. */
export const TaskMessagesResponseSchema = z.object({
  messages: z.array(TaskMessageSchema),
});
export type TaskMessagesResponse = z.infer<typeof TaskMessagesResponseSchema>;

/** Case-insensitive email equality (`a` may be null = no match). */
function sameEmail(a: string | null, b: string): boolean {
  return a !== null && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Which register a **live** task belongs to, from `viewerEmail`'s seat — the
 * heart of the viewer-relative model. One Task is one object seen from two
 * sides: a to-do reads *Waiting on them* for the assigner and *Needs my action*
 * for the assignee, computed here, never stored.
 *
 * Returns `null` when the task does not belong on this viewer's Surface — a
 * to-do strictly between *other* operators. (Watches are a transparency
 * broadcast, so they always resolve to a register for every viewer; to-dos are
 * work between specific people and only surface to those people.)
 *
 * Assumes the task is live; the aggregator filters cleared tasks
 * (`completed !== null`) before grouping.
 */
export function registerFor(
  task: Pick<Task, "assignee" | "assigner" | "status">,
  viewerEmail: string,
): TaskRegister | null {
  // Watch — a transparency broadcast: mine, or someone else's that I can see.
  if (!hasAssignee(task.assignee)) {
    return sameEmail(task.assigner, viewerEmail)
      ? "im-watching"
      : "theyre-watching";
  }

  // To-do — only surfaces to the assigner and the assignee.
  const viewerIsAssignee = sameEmail(task.assignee, viewerEmail);
  const viewerIsAssigner = sameEmail(task.assigner, viewerEmail);
  if (!viewerIsAssignee && !viewerIsAssigner) return null;

  const isDone = task.status === "done";

  // Self-assigned to-do: mine to do, and mine to confirm — always my action.
  if (viewerIsAssignee && viewerIsAssigner) return "needs-my-action";

  // I'm the assignee: my action until I mark it done, then I wait on review.
  if (viewerIsAssignee) return isDone ? "waiting-on-them" : "needs-my-action";

  // I'm the assigner: I wait while it's open; once done it's mine to review.
  return isDone ? "needs-my-action" : "waiting-on-them";
}
